import { DEFAULT_LOOK, FONTS, LookPrefs, cleanLook, safeThemeId } from "../shared/look_prefs";
import { RGB, luminance, mix, parseHex, readable, rgba } from "../shared/color";
import { themeById } from "../shared/themes";

// paints a person's look prefs on top of the theme: attributes the css keys off, scale variables for the
// atmosphere layers, and inline colour tokens when they picked their own accent or background.
// the theme stays underneath, clearing a pref hands the theme back untouched

const KEY = "chix-look-prefs";
// everything set inline, so a clear pass leaves nothing from the last look behind
const INLINE = [
  "--bg", "--panel-solid", "--panel-hi", "--well", "--ink", "--ink-2", "--ink-3", "--line", "--line-2", "--fog-a", "--fog-b", "--scrim", "--shadow",
  "--chi", "--chi-ink", "--chi-dim", "--glow", "--glow-ink",
  "--font-display", "--font-label", "--font-body",
  "--k-grain", "--k-fog", "--k-pat", "--k-vig", "--k-filter", "--ui-zoom", "color-scheme"
];

const WHITE: RGB = [255, 255, 255];
const BLACK: RGB = [0, 0, 0];
const CJK = '"M PLUS 1p", "Hiragino Sans", "Yu Gothic UI", Meiryo, sans-serif';

let current: LookPrefs = { ...DEFAULT_LOOK };

export function currentLook(): LookPrefs {
  return current;
}

export function readSavedLook(): LookPrefs {
  try {
    return cleanLook(JSON.parse(localStorage.getItem(KEY) || "{}"));
  } catch {
    return { ...DEFAULT_LOOK };
  }
}

// border strength as the alpha of the two line colours
function lineAlpha(border: string): [number, number] {
  return border === "faint" ? [0.07, 0.16] : border === "bold" ? [0.26, 0.48] : [0.13, 0.26];
}

function paintBackground(style: CSSStyleDeclaration, bg: RGB, border: string, high: boolean): boolean {
  const light = luminance(bg) > 0.4;
  // high contrast pushes the quiet greys up and the lines out, whatever border strength was picked
  const [soft, hard] = high ? [0.4, 0.7] : lineAlpha(border);
  const solid = light ? mix(bg, WHITE, 0.55) : mix(bg, WHITE, 0.05);
  const ink = light ? mix(bg, BLACK, 0.92) : mix(bg, WHITE, 0.92);
  style.setProperty("--bg", `rgb(${bg.map(Math.round).join(", ")})`);
  style.setProperty("--panel-solid", `rgb(${solid.map(Math.round).join(", ")})`);
  style.setProperty("--panel-hi", rgba(WHITE, light ? 0.45 : 0.035));
  style.setProperty("--well", `rgb(${(light ? mix(bg, BLACK, 0.06) : mix(bg, BLACK, 0.45)).map(Math.round).join(", ")})`);
  style.setProperty("--ink", `rgb(${ink.map(Math.round).join(", ")})`);
  const ink2 = high ? ink : light ? mix(bg, BLACK, 0.72) : mix(bg, WHITE, 0.68);
  const ink3 = high ? mix(bg, ink, 0.88) : light ? mix(bg, BLACK, 0.62) : mix(bg, WHITE, 0.52);
  style.setProperty("--ink-2", `rgb(${ink2.map(Math.round).join(", ")})`);
  style.setProperty("--ink-3", `rgb(${ink3.map(Math.round).join(", ")})`);
  style.setProperty("--line", rgba(ink, soft));
  style.setProperty("--line-2", rgba(ink, hard));
  style.setProperty("--fog-a", rgba(light ? WHITE : mix(bg, WHITE, 0.35), light ? 0.55 : 0.2));
  style.setProperty("--fog-b", rgba(light ? mix(bg, WHITE, 0.6) : mix(bg, WHITE, 0.55), light ? 0.45 : 0.1));
  style.setProperty("--scrim", light ? "rgba(40, 50, 70, 0.3)" : "rgba(2, 4, 8, 0.6)");
  style.setProperty("--shadow", light ? "0 24px 48px -30px rgba(20, 30, 50, 0.45)" : "0 24px 48px -28px rgba(0, 0, 0, 0.9)");
  return light;
}

function paintAccent(style: CSSStyleDeclaration, base: RGB, bg: RGB): void {
  const chi = readable(base, bg, 3);
  const glow = readable(base, [12, 15, 22], 4.5);
  const inkOn = (color: RGB) => (luminance(color) > 0.35 ? "#0b0d12" : "#ffffff");
  style.setProperty("--chi", `rgb(${chi.map(Math.round).join(", ")})`);
  style.setProperty("--chi-ink", inkOn(chi));
  style.setProperty("--chi-dim", rgba(chi, 0.16));
  style.setProperty("--glow", `rgb(${glow.map(Math.round).join(", ")})`);
  style.setProperty("--glow-ink", inkOn(glow));
}

export function applyLook(prefs: LookPrefs): void {
  current = prefs;
  const root = document.documentElement;
  const style = root.style;
  for (const name of INLINE) style.removeProperty(name);
  root.dataset.corners = prefs.corners;
  root.dataset.glass = prefs.glass;
  root.dataset.border = prefs.border;
  root.dataset.width = prefs.width;
  root.dataset.enter = prefs.enter;
  root.dataset.rail = prefs.rail;
  root.dataset.font = prefs.font;
  root.dataset.align = prefs.align;
  root.dataset.density = prefs.density;
  root.dataset.dock = prefs.dock;
  root.dataset.contrast = prefs.contrast;
  root.dataset.focus = prefs.focus;
  root.dataset.spacing = prefs.spacing;
  root.dataset.panel = prefs.panel;
  root.dataset.filter = prefs.filter;
  // the system cursor needs no rules, so the attribute only exists while a pack is on
  if (prefs.cursor && prefs.cursor !== "default") root.dataset.cursor = prefs.cursor;
  else delete root.dataset.cursor;
  root.dataset.caps = prefs.caps ? "on" : "off";
  root.dataset.glitch = prefs.glitch ? "on" : "off";
  root.dataset.worlds = prefs.worlds ? "on" : "off";
  root.toggleAttribute("data-custom-bg", Boolean(parseHex(prefs.bg)));

  style.setProperty("--k-grain", String(prefs.grain / 100));
  style.setProperty("--k-fog", String(prefs.fog / 100));
  style.setProperty("--k-pat", String(prefs.pattern / 100));
  style.setProperty("--k-vig", String(prefs.vignette / 100));
  style.setProperty("--k-filter", String(prefs.filterStrength / 100));
  if (prefs.scale !== 100) style.setProperty("--ui-zoom", String(prefs.scale / 100));

  const font = FONTS.find((item) => item.id === prefs.font);
  // an empty role keeps the theme's own face for it
  if (font?.display) style.setProperty("--font-display", `${font.display}, ${CJK}`);
  if (font?.label2) style.setProperty("--font-label", `${font.label2}, ${CJK}`);
  if (font?.body) style.setProperty("--font-body", `${font.body}, ${CJK}`);

  const theme = themeById(root.dataset.theme || "");
  const customBg = parseHex(prefs.bg);
  let tone = theme.tone;
  if (customBg) {
    tone = paintBackground(style, customBg, prefs.border, prefs.contrast === "high") ? "light" : "dark";
    style.setProperty("color-scheme", tone);
  }
  root.dataset.tone = tone;

  const accent = parseHex(prefs.accent);
  if (accent) {
    const bgNow = customBg || parseHex(getComputedStyle(root).getPropertyValue("--bg").trim()) || (tone === "light" ? WHITE : BLACK);
    paintAccent(style, accent, bgNow);
  }

  const bar = customBg ? prefs.bg : theme.bar;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", bar);
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // private mode
  }
}

// the theme to paint: the person's pick, or the day or night one when they follow the device
export function resolveThemeId(themeId: string, prefs: LookPrefs): string {
  if (!prefs.autoOn) return safeThemeId(themeId, prefs.mature);
  return matchMedia("(prefers-color-scheme: dark)").matches ? prefs.autoDark : prefs.autoLight;
}
